<template>
  <div v-if="isLoadedCompanyInputFields">
    <search-form
      :display-submit-button="false"
      v-model="formData"
      :is-unlogged="isUnlogged"
      :unlogged-infos="unloggedInfos"
      :is-prelogged="isPrelogged"
      @single="singleChanged"
      @double="doubleChanged"
    />

    <div class="offline-research">
      <b-row v-if="formData.mealContribution" class="justify-content-center">
        <p class="booking-info bg-light col-10 col-md-8 col-lg-6 text-center">
          {{ $t("booking.meal_contribution") }}
          {{ formData.mealContribution | currency }}
        </p>
      </b-row>

      <b-row>
        <b-col cols="12">
          <part
            class="row offline-travellers"
            :title="$t('offline.contact_details.title')"
          >
            <div
              v-for="(value, type) in formData.bedrooms"
              v-if="value.amount"
              :class="getRoomTypeClass"
            >
              <traveller-form
                v-for="n in value.amount"
                v-if="n"
                :index="n"
                :already-selected-travellers="alreadySelectedTravellers"
                :room-type="type"
                :manage-bed-type="true"
                :is-unlogged="isUnlogged"
                :unlogged-infos="unloggedInfos"
                :bedType="value.travellers[n - 1].bedType"
                @traveller="onChangeTraveller"
                @bedType="onChangeBedType"
              />
            </div>
          </part>
        </b-col>
      </b-row>

      <b-row v-if="isPrelogged">
        <hr />
      </b-row>

      <b-row v-else>
        <b-col>
          <b-form-checkbox
            id="notify-travellers"
            name="notify-travellers"
            v-model="formData.notifyTravellers"
          >
            {{ $t("_notifyTravellers") }}
          </b-form-checkbox>

          <hr />
        </b-col>
      </b-row>

      <b-row class="justify-content-between">
        <part
          class="col-12 col-lg-6 col-xl-6 priority"
          :title="$t('offline.research_priority.title')"
        >
          <span class="question">
            {{ $t("offline.research_priority.question") }}
          </span>

          <span
            v-for="option in priorityOptions"
            :key="option.id"
            class="option"
          >
            <input
              type="radio"
              name="research-priority"
              :value="option.value"
              v-model="formData.researchPriority"
              :id="option.value"
            />

            <label :for="option.value">{{ $t(option.label) }}</label>
          </span>
        </part>

        <part
          class="col-12 col-lg-6 col-xl-5 requests"
          :title="$t('offline.requests.title')"
        >
          <radio-input
            label="offline.requests.breakfast"
            name="breakfast"
            :options="getRadioOptions('breakfast')"
            @input="updateRadio('breakfast', $event)"
          />

          <radio-input
            label="offline.requests.catering"
            name="catering"
            :options="getRadioOptions('catering')"
            @input="updateRadio('catering', $event)"
          />

          <radio-input
            label="offline.requests.private_car_park"
            name="private_car_park"
            :options="getRadioOptions('carpark')"
            @input="updateRadio('carpark', $event)"
          />

          <radio-input
            label="offline.requests.internet"
            name="internet"
            :options="getRadioOptions('internet')"
            @input="updateRadio('internet', $event)"
          />

          <radio-input
            label="offline.requests.disabled"
            name="disabled"
            :options="getRadioOptions('disabled')"
            @input="updateRadio('disabled', $event)"
          />
        </part>
      </b-row>

      <hr />

      <b-row>
        <part
          class="col-12 email-cc"
          :class="isPrelogged ? 'col-lg-12' : 'col-lg-6'"
          :title="
            $t(this.isPrelogged ? '_email_copies_elior' : '_email_copies')
          "
        >
          <email-copies
            v-model="formData.emailCopies"
            @add="addEmail"
            :placeholder="
              this.isPrelogged
                ? '_enter_email_copie_elior'
                : '_enter_email_copie'
            "
          />
        </part>
      </b-row>

      <hr />

      <b-row>
        <b-col cols="12">
          <part class="extra" :title="$t('offline.extra.title')">
            <textarea-input
              name="extra"
              v-model="formData.extra"
              :placeholder="$t('_textarea.phrase.offline')"
            />
          </part>
        </b-col>
      </b-row>

      <hr />

      <part
        class="row justify-content-center refs"
        :title="$t('offline.refs.title')"
        v-if="formData.customFields.length"
      >
        <div
          v-for="field of formData.customFields"
          :key="field.id"
          class="col-12 refs-input col-md-6"
          :class="formData.customFields.length > 2 ? 'col-lg-4' : ''"
        >
          <component
            :is="getCustomFieldType(field.type)"
            v-model="field.value"
            :required="field.required"
            :label="field.label"
            :name="`custom-input${field.id}`"
            :placeholder="field.placeholder"
            :read-only="field.read_only"
            :options="field.options"
            options-label="label"
            @input="onInputCustomFields(field.id, $event)"
          />
        </div>
      </part>

      <b-row class="pt-4 justify-content-center">
        <div v-if="displayErrors" class="col-lg-8 errors alert-danger">
          <div v-for="error in errors" :key="error.id" class="error">
            <span class="label">
              {{
                getErrorLabelTranslation(error.field, isPrelogged) | capitalise
              }}
              :
            </span>
            {{ error.message }}
          </div>
        </div>

        <div v-if="displaySuccess" class="col-lg-8">
          <b-alert variant="success" show>
            {{ $t("offline.confirm.phrase") }}
          </b-alert>
        </div>

        <div v-if="isSubmitting" class="lds-grid">
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
        </div>
      </b-row>

      <b-row class="justify-content-center pb-4">
        <multi-manager-modal
          v-if="!isPrelogged"
          @input="formData.managers = $event"
        />

        <input
          v-if="!isUnloggedSubmitted && !isPrelogged"
          type="button"
          class="submit save btn-disable btn"
          :value="$t('_save_form')"
          @click="onSave"
          :disabled="isSubmitting"
        />

        <input
          v-if="!isUnloggedSubmitted"
          type="button"
          class="submit orange-white btn"
          :value="$t('_send_request')"
          @click="checkManagersValidation"
          :disabled="isSubmitting"
        />

        <div v-else-if="isUnloggedSubmitted && isUnlogged && !isPrelogged">
          <router-link
            :to="unloggedButtonRouterLink"
            class="mr-4 btn orange-white"
          >
            {{ $t("offline.confirm.new_booking") }}
          </router-link>

          <router-link
            class="btn orange-white"
            :to="{
              name: 'create_account',
              params: { companyUid: unloggedInfos.companyUniqueChain },
            }"
          >
            {{ $t("_create_account") | capitalize }}
          </router-link>
        </div>
      </b-row>
    </div>
  </div>
</template>

<script>
import { fieldTypes } from "@/utils/fieldTypeCodes";
import formatter from "@/components/global/mixins/formatterMethods";
import searchForm from "@/components/frontoffice/form/search";
import part from "@/components/frontoffice/part";
import numberInput from "@/components/frontoffice/form/baseInput/number";
import radioInput from "@/components/frontoffice/form/baseInput/radio";
import textareaInput from "@/components/frontoffice/form/baseInput/textArea";
import CommonApi from "@/api/commonApi";
import submitButton from "@/components/frontoffice/form/baseInput/submit";
import FormSubmitter from "@/api/formSubmitter";
import travellersMixin from "@/components/frontoffice/mixins/travellers";
import errorsLabelsTranslationMixin from "@/components/frontoffice/mixins/errorsLabelsTranslation";
import emailCopies from "@/components/frontoffice/form/emailCopies";
import textInput from "@/components/frontoffice/form/customInput/text";
import selectInput from "@/components/frontoffice/form/baseInput/select";
import multiManagerModal from "@/components/frontoffice/Booking/modal/multiManager";

export default {
  components: {
    searchForm,
    part,
    numberInput,
    radioInput,
    textareaInput,
    submitButton,
    emailCopies,
    textInput,
    selectInput,
    multiManagerModal,
  },

  mixins: [formatter, travellersMixin, errorsLabelsTranslationMixin],

  props: {
    duplicate: {
      type: Boolean,
      default: false,
    },
    draft: {
      type: Boolean,
      default: false,
    },
    edit: {
      type: Boolean,
      default: false,
    },
    isPrelogged: {
      type: Boolean,
      default: false,
    },
    isGeneric: {
      type: Boolean,
      default: false,
    },
    genericInfos: {
      type: Object,
      default() {
        return {
          companyInputFields: null,
          companyUniqueChain: null,
          onlyOne: false,
        };
      },
    },
    preloggedInfos: {
      type: Object,
      default() {
        return {
          companyInputFields: null,
          companyUniqueChain: null,
          onlyOne: false,
        };
      },
    },
  },

  data() {
    return {
      bookingId: this.$route.params.id,
      commonApi: new CommonApi(),
      formData: {
        duplicate: false,
        bookingId: null,
        destination: null,
        favorite: null,
        bedrooms: {
          single: {
            amount: 1,
            travellers: [],
          },
          double: {
            amount: 0,
            travellers: [],
          },
        },
        dates: {
          from: null,
          to: null,
        },
        services: {
          catering: {
            value: "catering",
            text: "home.form.services.catering",
            selected: false,
          },
          carpark: {
            value: "carpark",
            text: "home.form.services.carpark",
            selected: false,
          },
          breakfast: {
            value: "breakfast",
            selected: false,
          },
          disabled: {
            value: "disabled",
            selected: false,
          },
          internet: {
            value: "internet",
            selected: false,
          },
        },
        researchPriority: "location",
        extra: "",
        customFields: [],
        emailCopies: [""],
        managers: [],
        notifyTravellers: true,
        isDraft: false,
      },
      priorityOptions: [
        { value: "budget", label: "offline.research_priority.budget" },
        { value: "location", label: "offline.research_priority.location" },
      ],
      displayErrors: false,
      displaySuccess: false,
      displaySave: false,
      errors: [],
      isLoadedCompanyInputFields: false,
      isUnloggedSubmitted: false,
      booking: null,
      isNewForm: false,
      isSubmitting: false,
      isEditOffline: false,
    };
  },

  created () {
    if (this.bookingId) {
      this.isEditOffline = true;
      this.formData.duplicate = this.duplicate
      this.formData.bookingId = this.bookingId
      this.initExistingBooking();
    } else this.initNewBooking();

    this.isNewForm = !(this.edit || this.duplicate || this.isEditOffline);
  },
  methods: {
    getCustomFieldType(type) {
      switch (type) {
        case fieldTypes["DROP_DOWN_MENU"]:
          return "selectInput";
        case fieldTypes["TEXT"]:
        default:
          return "textInput";
      }
    },

    addEmail() {
      this.formData.emailCopies.push("");
    },

    initNewBooking() {
      new Promise((resolve, reject) => {
        let data = {};

        if (!this.isUnlogged) {
          // standard form
          data.customFields = this.$store.getters["security/company"].inputFields;
          this.formData = this.$store.getters["research/form"];
        } else {
          // prelogged account OR generic
          this.formData.dates.from = new Date();
          this.formData.favorite = {
            location: null,
            hotel: null,
          };

          this.formData.mealContribution = this.unloggedInfos.mealContribution;
          data.customFields = this.unloggedInfos.companyInputFields;
          // Breakfast & internet are true by default
          this.formData.services.breakfast = {
            selected: true,
            value: "breakfast",
          };
          this.formData.services.internet = {
            selected: true,
            value: "internet",
          };

          if (this.isPrelogged) this.formData.services.catering.selected = true;
        }

        this.formData.emailCopies = [
          this.$store.getters["security/email2"] || "",
        ];
        this.formData.notifyTravellers =
          this.$store.getters["security/sendVoucherToTravellers"];

        resolve(data);
      }).then((data) => {
        this.initFormDataRooms(
          this.isPrelogged ? 1 : this.formData.bedrooms.single.amount,
          this.isPrelogged ? 0 : this.formData.bedrooms.double.amount
        );
        this.setCompanyInputFields(data.customFields);
      });
    },

    async initExistingBooking() {
      await new Promise(async (resolve, reject) => {
        let data = {};
        this.commonApi
          .searchBooking(this.bookingId, this.edit)
          .then((response) => {
            if (this.edit && !response.data.data.isEditable) {
              return;
            }

            this.booking = response.data.data.booking;
            this.formatBookingFromApi(this.booking);
            data.rooms = this.booking.hotel_rooms;
            let nbRooms = {
              single: this.booking.single_rooms,
              double: this.booking.double_rooms
            };

            data.nbSingleRooms = nbRooms.single;
            data.nbDoubleRooms = nbRooms.double;
          });
        this.commonApi.bookingCustomFields(this.bookingId).then((response) => {
          data.customFields = response.data.data;
          resolve(data);
        });
      }).then((data) => {
        this.initFormDataRooms(
          data.nbSingleRooms,
          data.nbDoubleRooms,
          data.rooms
        );
        this.setCompanyInputFields(data.customFields);
      });
    },

    setCompanyInputFields(resp) {
      resp.forEach((value,index) => {
        if (resp.hasOwnProperty(index)) {
          let field = resp[index];
          if (this.isNewForm) {
            // Set default values
            switch (field.type) {
              case fieldTypes["NUMERIC"]:
                field.value = field.default_value ? field.default_value : 0;
                break;
              case fieldTypes["BOOLEAN"]:
                field.options.forEach((content, i) => {
                  if (field.options.hasOwnProperty(i)) {
                    let option = field.options[i];
                    option.selected = i === 0;
                  }
                })
                break;
              case fieldTypes["DROP_DOWN_MENU"]:
                field.value = null;
                break;
              case fieldTypes["TEXT"]:
              default:
                field.value = field.default_value ? field.default_value : "";
                break;
            }
          } else {
            field = Object.assign(field.input_field, {
              value: field.value
            })
          }
          this.formData.customFields[index] = field;
        }
      })
      this.isLoadedCompanyInputFields = true;
    },

    getRadioOptions(key) {
      let val = this.formData.services[key].selected;

      return [
        { value: "yes", label: "_yes", selected: val },
        { value: "no", label: "_no", selected: !val },
      ];
    },

    updateRadio(radioItem, newVal) {
      this.formData.services[radioItem].selected = newVal === "yes";
    },

    onSave() {
      this.formData.isDraft = true;
      this.formData.bookingId = this.bookingId;
      this.checkManagersValidation();
    },

    checkManagersValidation() {
      // let preval = false
      // let postval = false
      // let postvalPolicyDontSuit = false
      //
      // if (this.$store.getters['security/company']) {
      //   preval = this.$store.getters['security/company'].managerPreVal
      //   postval = this.$store.getters['security/company'].managerPostVal
      //   postvalPolicyDontSuit = this.$store.getters['security/company'].managerPostValPolicyDontSuit
      // }
      //
      // if (!preval && !postval && !postvalPolicyDontSuit)
      //   this.onSubmit()
      // else if (!this.$store.getters['security/company'].multiManagerEnabled)
      //   this.onSubmit()
      // else if (this.formData.managers.length)
      //   this.onSubmit()
      // else this.$bvModal.show('multi-manager-modal')
      if (this.$store.getters['security/company']) {
        if (!this.$store.getters['security/company'].multiManagerEnabled || this.formData.managers.length) { this.onSubmit() } else { this.$bvModal.show('multi-manager-modal') }
      } else {
        this.onSubmit()
      }
    },

    onSubmit() {
      // Only one submit
      if (this.isSubmitting) {
        console.log("query already submitted");
        return false;
      }

      // Disable button and block all others actions
      this.isSubmitting = true;

      // Hide errors
      this.displayErrors = false;
      this.errors = [];

      let type = this.isPrelogged
        ? "pre_logged_search"
        : this.isGeneric
        ? "generic_search"
        : "offline_research";

      if (this.isGeneric && this.user) {
        this.formData.user = this.user;
      }

      let submitter = new FormSubmitter(type);
      submitter
        .submitRequest(this.formData, this.$route.params.companyUid)
        .then((response) => {
          if (response["data"]["success"]) {
            this.dealSuccess(response["data"]["data"]["newId"]);
            if (this.isUnlogged) this.isUnloggedSubmitted = true;
          } else {
            this.dealFailure(response["data"]["errors"]);
          }
        });
    },

    onInputCustomFields(newVal) {
      this.formData.customFields.map(function (customField) {
        if (customField.id === newVal.id) {
          customField.value = newVal.value;
        }
      });
    },

    dealSuccess(newId) {
      this.displayErrors = false;

      // Reset search criterias
      this.$store.commit("research/reset");
      if (this.formData.isDraft === true) {
        this.isSubmitting = false;
        this.$router.push({
          name: "bookings",
          params: {
            id: newId,
            displaySave: this.formData.isDraft,
          },
        });
      } else {
        if (this.$route.params.companyUid) {
          this.displaySuccess = true;
          this.$store.commit("generic/addBooking", newId);
        } else {
          this.$router.push({
            name: "booking_request_confirm",
            params: {
              id: newId,
              requireValidation:
                this.$store.getters["security/company"].managerPreVal,
            },
          });
        }
      }
    },

    dealFailure(response) {
      this.errors = [];
      let managedErrors = [];

      for (let fields in response) {
        if (response.hasOwnProperty(fields)) {
          let code = parseInt(response[fields]);
          let message = isNaN(code)
            ? response[fields]
            : this.$i18n.t("error." + code);
          let listFields = [];

          if (fields.indexOf(",") !== -1) {
            listFields = fields.split(",");
          } else {
            listFields = [fields];
          }

          for (let field of listFields) {
            if (managedErrors.indexOf(field) === -1) {
              managedErrors.push(field);
              this.errors.push({
                field: field,
                message: message,
              });
            }
          }
        }
      }

      this.displayErrors = true;
      this.isSubmitting = false;
    },

    formatBookingFromApi(data) {
      this.formData.destination = data.desired_city
        ? {
            locationName: data.desired_city,
          }
        : null;

      this.formData.favorite = {
        location: data.desired_location || null,
        hotel: data.desired_hotel || null,
      };

      this.formData.dates = {
        from: data.arrival_date,
        to: data.departure_date,
      };

      this.formData.services.catering.selected = data.restaurant;
      this.formData.services.carpark.selected = data.private_car_park;
      this.formData.services.breakfast.selected = data.breakfast_included;
      this.formData.services.disabled.selected = data.disabled_access;
      this.formData.services.internet.selected = data.internet;

      this.formData.researchPriority = data.research_priority;

      this.formData.extra = data.comment;

      if (data.email_c_c) {
        let emails = data.email_c_c.split(",");
        this.formData.emailCopies = emails.map((email) => email.trim());
      }
      this.formData.notifyTravellers = data.notify_travellers;
    },

    singleChanged(val) {
      this.updateFormDataRooms("single", val);
    },

    doubleChanged(val) {
      this.updateFormDataRooms("double", val);
    },
  },

  computed: {
    onlyOne() {
      return this.isUnlogged
        ? this.unloggedInfos.onlyOne
        : this.$store.getters["security/company"].onlyOne;
    },

    isUnlogged() {
      return this.isPrelogged || this.isGeneric;
    },

    unloggedInfos() {
      return this.isPrelogged
        ? this.preloggedInfos
        : this.isGeneric
        ? this.genericInfos
        : null;
    },

    unloggedButtonRouterLink() {
      if (!this.isUnlogged) return null;

      if (this.isGeneric) {
        return {
          name: "home_generic_company",
          params: { companyUid: this.$route.params.companyUid },
        };
      }

      if (this.isPrelogged) {
        return {
          name: "offline_booking_prelogged_company",
          params: { companyUid: this.$route.params.companyUid },
        };
      }
    },
  },
};
</script>

<style lang="scss">
.submit {
  margin: 2rem auto;
  display: block;
}

.save {
  color: white;
}

.required {
  &:after {
    content: "*";
  }
}

.errors {
  margin: 2rem auto;
  padding: 5px 10px;

  .error {
    .label {
      font-weight: bold;
    }
  }
}
.lds-grid {
  margin: 17px;
}
</style>
